import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteScrollObserver } from '@/hooks/useInfiniteScrollObserver';
import { streakQueryOptions } from '@/queries/streak/streak.queries';
import { expandStreakAwards, getStreakFocusDays, isStreakFocusLoaded } from '../utils/streak.utils';

/** Loads streak rewards and positions the initial view around completed and upcoming days. */
export const useGamificationStreak = () => {
  const { data: streakData, isPending: streakPending } = useQuery(streakQueryOptions.me());
  const currentStreak = Math.max(0, streakData?.currentStreak ?? 0);
  const isCountedToday = streakData?.isCountedToday ?? false;
  const showStartCard = !!streakData && currentStreak === 0 && !isCountedToday;
  const {
    data: awardsData,
    fetchNextPage,
    hasNextPage,
    isPending: awardsPending,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useInfiniteQuery({ ...streakQueryOptions.xpAwardsInfinite(), enabled: !!streakData && !showStartCard });
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  // Remember the aligned streak state so later page loads do not reset manual scrolling.
  const alignedDayRef = useRef<string | null>(null);

  const setScrollRoot = useCallback((node: HTMLDivElement | null) => {
    // The scroll root appears after the loading skeleton is replaced by the slider.
    if (node) {
      setScrollContainer(node);
    }
  }, []);

  const loadMore = useCallback(() => {
    void fetchNextPage({ cancelRefetch: false });
  }, [fetchNextPage]);

  const awards = useMemo(() => awardsData?.pages.flatMap(page => page.items) ?? [], [awardsData]);
  // API awards can span several days; the slider renders one card per day with XP.
  const dailyAwards = useMemo(
    () => expandStreakAwards(awards, currentStreak).filter(({ xp }) => xp > 0).sort((a, b) => a.day - b.day),
    [awards, currentStreak],
  );
  const focus = useMemo(
    () => getStreakFocusDays(dailyAwards, currentStreak, isCountedToday),
    [dailyAwards, currentStreak, isCountedToday],
  );
  const focusLoaded = isStreakFocusLoaded(awards, currentStreak, isCountedToday, focus);

  // Fetch until the active day, or three upcoming days after today's completion, are available.
  useEffect(() => {
    if (!streakData || awardsPending || focusLoaded || !hasNextPage || isFetchingNextPage || isFetchNextPageError) {
      return;
    }

    loadMore();
  }, [streakData, awardsPending, focusLoaded, hasNextPage, isFetchingNextPage, isFetchNextPageError, loadMore]);

  // On mobile, align the first card in the chosen group with the heading.
  // On desktop, center the active card or the boundary between completed and upcoming cards.
  useLayoutEffect(() => {
    const focusKey = `${currentStreak}:${isCountedToday}`;

    if (!scrollContainer || alignedDayRef.current === focusKey || !dailyAwards.length) {
      return;
    }

    const scrollRect = scrollContainer.getBoundingClientRect();
    const isDesktop = window.matchMedia?.('(min-width: 768px)').matches ?? window.innerWidth >= 768;
    const getCard = (day: number | undefined) => day === undefined
      ? null
      : scrollContainer.querySelector<HTMLElement>(`[data-streak-day="${day}"]`);

    if (!isDesktop) {
      const card = getCard(focus.mobileStartDay);

      if (!card) {
        return;
      }

      const headingLeft = headingRef.current?.getBoundingClientRect().left ?? scrollRect.left;
      scrollContainer.scrollLeft += card.getBoundingClientRect().left - headingLeft;
    } else {
      const activeCard = getCard(focus.activeDay);
      const completedCard = getCard(focus.lastCompletedDay);
      const upcomingCard = getCard(focus.firstUpcomingDay);
      let focusCenter: number | undefined;

      if (activeCard) {
        const rect = activeCard.getBoundingClientRect();
        focusCenter = rect.left + rect.width / 2;
      } else if (completedCard && upcomingCard) {
        focusCenter = (completedCard.getBoundingClientRect().right + upcomingCard.getBoundingClientRect().left) / 2;
      } else {
        const card = upcomingCard ?? completedCard;

        if (card) {
          const rect = card.getBoundingClientRect();
          focusCenter = rect.left + rect.width / 2;
        }
      }

      if (focusCenter === undefined) {
        return;
      }

      scrollContainer.scrollLeft += focusCenter - (scrollRect.left + scrollContainer.clientWidth / 2);
    }

    // Only lock the position after the required awards are loaded or pagination ends.
    if (focusLoaded || !hasNextPage) {
      alignedDayRef.current = focusKey;
    }
  }, [scrollContainer, currentStreak, isCountedToday, dailyAwards.length, focus, focusLoaded, hasNextPage]);

  // Observe the slider itself so vertical page scrolling cannot trigger pagination.
  const observerOptions = useMemo(() => ({
    root: scrollContainer,
    rootMargin: '0px 400px 0px 0px',
  }), [scrollContainer]);

  const loaderRef = useInfiniteScrollObserver<HTMLDivElement>({
    hasNextPage: !!scrollContainer && hasNextPage && !isFetchNextPageError,
    isPending: isFetchingNextPage,
    onLoadMore: loadMore,
    observerOptions,
  });

  // Keep the skeleton visible while fetching pages needed for the initial position.
  const isLoadingFocusAwards = awardsPending || (
    !focusLoaded && hasNextPage && !isFetchNextPageError
  );
  const isPending = streakPending || (!showStartCard && isLoadingFocusAwards);

  return {
    currentStreak,
    isCountedToday,
    dailyAwards,
    hasNextPage,
    isFetchNextPageError,
    isPending,
    hasStreakData: !!streakData,
    showStartCard,
    headingRef,
    loaderRef,
    setScrollRoot,
  };
};
